/** Threshold above which we switch from relative to absolute display */
const THRESHOLD_24H_MS = 24 * 60 * 60 * 1000;

/** Grouping threshold: consecutive same-sender messages within this window share a timestamp group */
export const GROUP_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Format a timestamp string for display.
 * - Recent (<24h): "just now", "2m ago", "1h ago"
 * - Older (>=24h): "Mar 14, 3:45 PM"
 * Returns empty string if timestamp is missing/invalid.
 */
export function formatTimestamp(timestamp: string | undefined): string {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '';

  const diffMs = Date.now() - date.getTime();

  if (diffMs < THRESHOLD_24H_MS && diffMs >= 0) {
    if (diffMs < 60_000) return 'just now';
    if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`;
    return `${Math.floor(diffMs / 3_600_000)}h ago`;
  }

  // Absolute for older messages or future timestamps (edge case)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Determine if a timestamp should be shown for the message at `index`.
 * Only shows on the last message in a consecutive same-sender group
 * (next message is different sender OR more than GROUP_THRESHOLD_MS later).
 */
export function shouldShowTimestamp(
  messages: ReadonlyArray<{ role: string; timestamp?: string }>,
  index: number,
): boolean {
  const message = messages[index];
  const nextMessage = messages[index + 1];

  // Always show on last message
  if (!nextMessage) return true;

  // Different sender → end of group
  if (nextMessage.role !== message.role) return true;

  // Both have timestamps: check gap
  if (message.timestamp && nextMessage.timestamp) {
    const thisTime = new Date(message.timestamp).getTime();
    const nextTime = new Date(nextMessage.timestamp).getTime();
    if (!isNaN(thisTime) && !isNaN(nextTime) && Math.abs(nextTime - thisTime) > GROUP_THRESHOLD_MS) {
      return true;
    }
  }

  return false;
}
